/**
 * View: Reward Catalog Create/Edit
 * Form for creating new rewards or editing existing ones with dynamic fields based on reward type.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import { RewardType, type VoucherDetail } from "../../../domain/model/RewardCatalogModel";
import { SuccessModal } from "@/components/ui/success-modal";
import { RewardTypeSelectorSection } from "../components/RewardTypeSelectorSection/RewardTypeSelectorSection";
import { VoucherFieldsSection } from "../components/VoucherFieldsSection/VoucherFieldsSection";
import { EWalletFieldsSection } from "../components/EWalletFieldsSection/EWalletFieldsSection";
import { PointConversionFieldsSection } from "../components/PointConversionFieldsSection/PointConversionFieldsSection";
import { VoucherkuFieldsSection } from "../components/VoucherkuFieldsSection/VoucherkuFieldsSection";
import { RewardCommonFieldsSection } from "../components/RewardCommonFieldsSection/RewardCommonFieldsSection";
import { RewardFormActionsSection } from "../components/RewardFormActionsSection/RewardFormActionsSection";
import {
  RewardCatalogCreateViewModel,
  initialFormData,
  type RewardFormData,
  type RewardFormErrors,
} from "../../viewmodel/RewardCatalogCreateViewModel";

export const RewardCatalogCreateView = (): React.JSX.Element => {
  const { id: voucherId } = useParams<{ id: string }>();
  const isEditMode = Boolean(voucherId);

  usePageTitle(isEditMode ? "Edit Reward" : "Create Reward");

  const navigate = useNavigate();
  const [formData, setFormData] = useState<RewardFormData>(initialFormData);
  const [originalData, setOriginalData] = useState<VoucherDetail | null>(null);
  const [errors, setErrors] = useState<RewardFormErrors>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [voucherCodes, setVoucherCodes] = useState<readonly string[]>([]);

  const {
    fetchVoucherDetail,
    handleInputChange: viewModelHandleInputChange,
    handleTypeChange,
    validateForm,
    submitForm,
    updateVoucherCodesFromValidation,
  } = RewardCatalogCreateViewModel({
    setFormData,
    setOriginalData,
    setErrors,
    setShowSuccessModal,
    setIsSubmitting,
    setIsLoading,
    setSubmitError,
    setVoucherCodes,
  });

  // Fetch voucher detail when in edit mode
  useEffect(() => {
    if (voucherId) {
      fetchVoucherDetail(voucherId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherId]);

  const handleInputChange = (field: keyof RewardFormData, value: string) => {
    viewModelHandleInputChange(errors, field, value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm(formData)) {
      return;
    }

    await submitForm(formData, originalData, voucherCodes, isEditMode, voucherId);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/swarapoint/reward-catalog");
  };

  const handleCancel = () => {
    navigate("/swarapoint/reward-catalog");
  };

  // Show loading state when fetching voucher detail in edit mode
  if (isLoading) {
    return (
      <MainLayout
        title={isEditMode ? "Edit Reward" : "Add New Reward"}
        onBack={() => navigate("/swarapoint/reward-catalog")}
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      title={isEditMode ? "Edit Reward" : "Add New Reward"}
      onBack={() => navigate("/swarapoint/reward-catalog")}
    >
      <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
        <RewardTypeSelectorSection
          type={formData.type}
          title={formData.title}
          titleError={errors.title}
          voucherkuItem={formData.voucherkuItem}
          voucherkuItemError={errors.voucherkuItem}
          voucherkuCategoryId={formData.voucherkuCategoryId}
          onChangeType={handleTypeChange}
          onChangeTitle={(value) => handleInputChange("title", value)}
          onChangeVoucherkuItem={(value) =>
            handleInputChange("voucherkuItem", value)
          }
          onAutoFillVoucherku={(data) => {
            handleInputChange("title", data.title);
            handleInputChange("description", data.description);
            handleInputChange("quantity", data.quantity);
            handleInputChange("voucherkuCategoryId", data.categoryId);
          }}
          isEditMode={isEditMode}
        />

        {formData.type === RewardType.VOUCHER && (
          <VoucherFieldsSection
            category={formData.category}
            categoryError={errors.category}
            categoryDisabled={isEditMode}
            onChangeCategory={(value) => handleInputChange("category", value)}
            description={formData.description}
            descriptionError={errors.description}
            termsAndConditions={formData.termsAndConditions}
            termsAndConditionsError={errors.termsAndConditions}
            bannerImage={formData.bannerImage}
            bannerImageError={errors.bannerImage}
            voucherCode={formData.voucherCode}
            voucherCodeError={errors.voucherCode}
            onChangeDescription={(value) =>
              handleInputChange("description", value)
            }
            onChangeTermsAndConditions={(value) =>
              handleInputChange("termsAndConditions", value)
            }
            onChangeBannerImage={(value) =>
              handleInputChange("bannerImage", value)
            }
            onChangeVoucherCode={(value) =>
              handleInputChange("voucherCode", value)
            }
            onVoucherCodeValidationComplete={updateVoucherCodesFromValidation}
          />
        )}

        {formData.type === RewardType.E_WALLET_BALANCE && (
          <EWalletFieldsSection
            eWallet={formData.eWallet}
            eWalletError={errors.eWallet}
            amount={formData.pointsAmount}
            amountError={errors.pointsAmount}
            startDate={formData.startDate}
            startDateError={errors.startDate}
            endDate={formData.endDate}
            endDateError={errors.endDate}
            onChangeEWallet={(value) => handleInputChange("eWallet", value)}
            onChangeAmount={(value) => handleInputChange("pointsAmount", value)}
            onChangeStartDate={(value) => handleInputChange("startDate", value)}
            onChangeEndDate={(value) => handleInputChange("endDate", value)}
          />
        )}

        {formData.type === RewardType.POINT_CONVERSION && (
          <PointConversionFieldsSection
            pointProvider={formData.pointProvider}
            pointProviderError={errors.pointProvider}
            amount={formData.pointsAmount}
            amountError={errors.pointsAmount}
            startDate={formData.startDate}
            startDateError={errors.startDate}
            endDate={formData.endDate}
            endDateError={errors.endDate}
            onChangePointProvider={(value) =>
              handleInputChange("pointProvider", value)
            }
            onChangeAmount={(value) => handleInputChange("pointsAmount", value)}
            onChangeStartDate={(value) => handleInputChange("startDate", value)}
            onChangeEndDate={(value) => handleInputChange("endDate", value)}
          />
        )}

        {formData.type === RewardType.VOUCHERKU && (
          <VoucherkuFieldsSection
            title={formData.title}
            titleError={errors.title}
            description={formData.description}
            descriptionError={errors.description}
            termsAndConditions={formData.termsAndConditions}
            termsAndConditionsError={errors.termsAndConditions}
            bannerImage={formData.bannerImage}
            bannerImageError={errors.bannerImage}
            onChangeTitle={(value) => handleInputChange("title", value)}
            onChangeDescription={(value) =>
              handleInputChange("description", value)
            }
            onChangeTermsAndConditions={(value) =>
              handleInputChange("termsAndConditions", value)
            }
            onChangeBannerImage={(value) =>
              handleInputChange("bannerImage", value)
            }
          />
        )}

        {(formData.type === RewardType.VOUCHER ||
          formData.type === RewardType.VOUCHERKU) && (
          <RewardCommonFieldsSection
            type={formData.type}
            pointsAmount={formData.pointsAmount}
            pointsAmountError={errors.pointsAmount}
            quantity={formData.quantity}
            quantityError={errors.quantity}
            quantityDisabled={formData.type === RewardType.VOUCHER}
            validityPeriod={formData.validityPeriod}
            validityPeriodError={errors.validityPeriod}
            validityPeriodDisabled={true}
            merchant={formData.merchant}
            merchantError={errors.merchant}
            merchantDisabled={isEditMode}
            startDate={formData.startDate}
            startDateError={errors.startDate}
            endDate={formData.endDate}
            endDateError={errors.endDate}
            onChangePointsAmount={(value) =>
              handleInputChange("pointsAmount", value)
            }
            onChangeQuantity={(value) => handleInputChange("quantity", value)}
            onChangeValidityPeriod={(value) =>
              handleInputChange("validityPeriod", value)
            }
            onChangeMerchant={(value) => handleInputChange("merchant", value)}
            onChangeStartDate={(value) => handleInputChange("startDate", value)}
            onChangeEndDate={(value) => handleInputChange("endDate", value)}
          />
        )}

        {/* Error Message */}
        {submitError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{submitError}</p>
          </div>
        )}

        <RewardFormActionsSection
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </form>

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Success"
        message={
          isEditMode ? "Reward has been updated." : "New reward has been added."
        }
      />
    </MainLayout>
  );
};
